import {
  ANTIWASTE,
  ANTIWASTES,
  RAYONS,
  VALUE,
  VALUES,
  NEW,
  FRESH,
  PRODUCT_MULTIPLE,
  DIETS,
  DIET,
  HIDDEN_PRICE,
  EARLY_EXPIRATION,
  ULTRA_FRESH,
  VALUES_DE,
  PRODUCT_SUBTYPE_DE,
  MINIMUM_ADD_TO_CART_QUANTITY,
} from '../constants/algolia';
import {
  IProduct,
  IFinance,
  IShopifyProduct,
  IMarketing,
  IShoppingCartProduct,
  ISearchProduct,
  EcommerceProductData,
  ShoppingCartProductType,
  IOrderItem,
  IPrismicProduct,
  ProductPricingFeatures,
} from '@monofront/types';
import {
  Channel,
  FIRST_ORDER_GIFT,
  FRESH_USER,
  ULTRA_FRESH_USER,
} from '../constants';
import { format } from './price';
import { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import Decimal from 'decimal.js';
import { getBundleProductDetails } from './algolia';

type Refinement = CurrentRefinementsConnectorParamsItem;

const AND = 'AND';
const CONTAINS = 'CONTAINS';

export const formatPrismicProduct = (product: IShopifyProduct): IProduct => ({
  title: product.title,
  updated_at: product.updated_at,
  _tags: product.tags.split(/,\s?/),
  handle: product.handle,
  vendor: product.vendor,
  product_type: product.product_type,
  template_suffix: product.template_suffix,
  sku: product.variants[0].sku,
  barcode: product.variants[0].barcode,
  option1: product.variants[0].option1 ?? null,
  option2: product.variants[0].option2 ?? null,
  option3: product.variants[0].option3 ?? null,
  position: product.variants[0].position,
  requires_shipping: product.variants[0].requires_shipping,
  taxable: product.variants[0].taxable,
  inventory_quantity: product.variants[0].inventory_quantity,
  id: product.id,
  tags: product.tags.split(/,\s?/),
  variants_count: 1,
  variants_min_price: Number(product.variants[0].price),
  variants_max_price: Number(product.variants[0].price),
  variants_inventory_count: product.variants[0].inventory_quantity,
  product_image: product.image?.src,
  published_at: product.published_at,
  body_html_safe: product.body_html,
  inventory_policy: product.variants[0].inventory_policy,
  inventory_management: product.variants[0].inventory_management,
  inventory_available: Boolean(product.variants[0].inventory_quantity),
  options: product.options,
  price: Number(product.variants[0].price),
  compare_at_price: Number(product.variants[0].compare_at_price),
  price_ratio:
    Number(product.variants[0].compare_at_price) /
    Number(product.variants[0].price),
  grams: product.variants[0].grams,
  weight: String(product.variants[0].weight),
  image: product.image?.src,
  images: product.images.map((image) => image.src),
  created_at: product.created_at,
  collections: [],
  collection_ids: [],
  meta: {},
  objectID: String(product.variants[0].id),
  values: [],
  diets: [],
});

export const formatPrismicProductToSearchProduct = (
  product: IPrismicProduct & IProduct,
): ISearchProduct => {
  const {
    diets,
    handle,
    id,
    image,
    images,
    meta,
    product_type,
    sku,
    tags,
    title,
    values,
    vendor,
  } = product;

  const [{ compare_at_price, grams, inventory_quantity, price, weight }] =
    product.variants;

  return {
    compare_at_price: Number(compare_at_price),
    diets: diets || [],
    grams,
    handle,
    id,
    inventory_available: inventory_quantity > 0,
    inventory_quantity,
    image: image?.src,
    images: images.map((image) => image.src),
    meta: meta || {},
    objectID: sku,
    price: Number(price),
    product_type,
    sku,
    tags: tags?.split(', '),
    title,
    values: values || [],
    vendor,
    weight: weight?.toString(),
  };
};

export const formatWeight = (weight: string | number): string => {
  let w: number;

  if (typeof weight === 'string') {
    w = parseFloat(weight.split('kg')[0]);
  } else {
    w = weight;
  }
  if (w < 1) {
    return (
      Math.round(1000 * w)
        .toString()
        .replace('.', ',') + ' g'
    );
  }

  return w?.toString().replace('.', ',') + ' kg';
};

/**
 * Compare if tags defined on Prismic matching product tags
 * in order to return the corresponding slice.
 */
export const getPrismicContent = (
  prismicContent: any,
  product: Pick<IProduct, 'tags'>,
) => {
  const compareTags = (item: any, tags: any) => {
    if (item.tag) {
      return item.match === CONTAINS
        ? tags.some((tag: any) => tag.startsWith(item.tag))
        : !tags.some((tag: any) => tag.startsWith(item.tag));
    }

    return false;
  };

  const content = prismicContent.find((element: any) => {
    return element.primary.condition_matcher === AND
      ? element.items.every((item: any) => compareTags(item, product.tags))
      : element.items.some((item: any) => compareTags(item, product.tags));
  });

  return content || prismicContent[0];
};

export const getFormattedProductWeight = (
  finance: IFinance | undefined,
  isShortFormat = true,
): {
  formattedWeight: string;
  drainedWeightTranslationKey: string;
} => {
  if (!finance || !finance?.product_format) {
    return {
      formattedWeight: '',
      drainedWeightTranslationKey: '',
    };
  }

  let formattedWeight = '';

  const { product_format, unit_of_measurement, product_format_with_liquid } =
    finance;

  const drainedWeightSuffix = product_format_with_liquid
    ? isShortFormat
      ? 'drainedWeightSuffixShort'
      : 'drainedWeightSuffix'
    : '';

  if (unit_of_measurement === 'kg') {
    formattedWeight = formatWeight(product_format);
  }
  if (unit_of_measurement?.toUpperCase() === 'L') {
    const productFormat = Number(product_format);

    if (productFormat < 1) {
      formattedWeight = `${(1000 * productFormat)
        .toString()
        .replace('.', ',')} mL`;
    } else {
      formattedWeight = `${productFormat
        .toFixed(2)
        .toString()
        .replace('.', ',')} L`;
    }
  }

  return {
    formattedWeight,
    drainedWeightTranslationKey: drainedWeightSuffix,
  };
};

const getMinimumDurabilityDateFromTags = (tags: any) => {
  const stringDate = tags
    .find((tag: any) => tag.startsWith(ANTIWASTES.STARTWITH))
    ?.split('_')?.[1];

  const isDate =
    /^((0?[1-9]|[12][0-9]|3[01])\/)?(0?[1-9]|1[012])\/\d{2,4}$/.test(
      stringDate,
    );

  if (!isDate) {
    return;
  }

  const [month, year] = stringDate.split('/').slice(-2);

  return `${month}/${year.slice(-2)}`;
};

export const getRecupTagFromTags = (
  tags: string[],
):
  | {
      translationKey: string;
      date?: string;
    }
  | undefined => {
  if (tags.includes(ANTIWASTE.ALL)) {
    return {
      translationKey: 'tagAntiwaste',
    };
  }

  const DDM = getMinimumDurabilityDateFromTags(tags);

  return DDM
    ? {
        translationKey: 'tagMinimumDurabilityDate',
        date: DDM,
      }
    : undefined;
};

export const getRefinements = (items: Refinement[]) => {
  const refinementsVendor = items.find((item) => item.attribute === 'vendor');
  const refinementsValues = items.find((item) => item.attribute === 'values');
  const refinementsDiets = items.find((item) => item.attribute === 'diets');
  const refinementsCategories = items.find(
    (item) => item.attribute === 'categories',
  );
  const refinementsShelfs = items.find((item) => item.attribute === 'shelfs');
  const refinementsNutriscore = items.find(
    (item) => item.attribute === 'meta.marketing.nutriscore_grade',
  );
  const refinementsEnvironmentalscore = items.find(
    (item) => item.attribute === 'meta.marketing.environmentalscore_grade',
  );

  return {
    refinementsVendor,
    refinementsValues,
    refinementsCategories,
    refinementsShelfs,
    refinementsNutriscore,
    refinementsEnvironmentalscore,
    refinementsDiets,
  };
};

export const getRefinementsOptions = (items: Partial<Refinement[]>) =>
  items.filter(Boolean);

export const getRefinementsScoresOptions = (items: Partial<Refinement[]>) =>
  items.filter(Boolean);

export const getWithNutrition = (marketing: IMarketing) =>
  Boolean(
    ('ingredients' in marketing && marketing.ingredients) ||
      ('product_values' in marketing && marketing.product_values) ||
      ('allergen' in marketing && marketing.allergen) ||
      ('nutritional_values_table_data' in marketing &&
        marketing.nutritional_values_table_data),
  );

export const getIsAntiWaste = (productTags: string[]): boolean =>
  productTags.some(
    (tag) =>
      tag.toUpperCase() ===
      `${VALUE.STARTWITH.toUpperCase()}${VALUES.ANTIWASTE}`,
  );

export const getFormattedValues = (
  productValues: string[],
  channel?: Channel,
): string[] => {
  const valuesAvailableHover = Object.values({
    ...(channel === Channel.DE ? VALUES_DE : VALUES),
  });

  if (!productValues) {
    return [];
  }

  return productValues.filter((tag) =>
    valuesAvailableHover.includes(tag.toUpperCase()),
  );
};

export const getFormattedGermanValues = (productValues: string[]): string[] => {
  const valuesAvailableHover = Object.values({
    ...VALUES_DE,
  });

  if (!productValues) {
    return [];
  }

  return productValues.filter((tag) =>
    valuesAvailableHover.includes(tag.toUpperCase()),
  );
};
export const getFormattedDiets = (productDiets: string[]): string[] => {
  const valuesAvailableHover = Object.values({
    ...DIETS,
  });

  if (!productDiets) {
    return [];
  }

  return productDiets.filter((tag) =>
    valuesAvailableHover.includes(tag.toUpperCase()),
  );
};

export const getIsGiftItem = ({ tags }: ISearchProduct): boolean =>
  Boolean(tags?.some((tag) => tag === FIRST_ORDER_GIFT));

export const getHasHiddenPrice = ({ tags }: ISearchProduct): boolean =>
  Boolean(tags?.some((tag) => tag === HIDDEN_PRICE.STARTWITH));

export const getUnitOfMeasurement = (product: ISearchProduct) =>
  product.meta.finance?.unit_of_measurement;

export const getFinance = (product: ISearchProduct) => product.meta?.finance;
export const getReviewsGlobal = (product: ISearchProduct) => ({
  average_rating:
    product.meta?.reviews_stats?.global?.average_rating?.toFixed(1) ?? null,
  count: product.meta?.reviews_stats?.global?.count ?? null,
});

export const getFormattedUnitPrice = (product: ISearchProduct) =>
  product.meta.finance?.unit_price
    ? format(product.meta.finance?.unit_price)
    : undefined;

export const getCurrentProductQuantity = (
  product: Pick<IProduct, 'objectID'>,
  shoppingCartProducts: IShoppingCartProduct[],
) =>
  shoppingCartProducts?.find(
    (shoppingCartProduct) => shoppingCartProduct.objectID === product.objectID,
  )?.quantity || 0;

export const getScoreColor = (score: number): string | undefined => {
  if (score <= 24) return '#ff0000';
  if (score <= 49 && score > 24) return '#ffa900';
  if (score <= 74 && score > 49) return '#6ab98b';
  if (score > 74) return '#35a664';

  return undefined;
};

export const getNutritionValues = (
  nutritionValuesTableData: string | undefined,
) => nutritionValuesTableData && JSON.parse(nutritionValuesTableData);

export const getDiscountPercent = ({
  price,
  compare_at_price,
}: Pick<ISearchProduct, 'price' | 'compare_at_price'>): number | undefined =>
  compare_at_price
    ? Number((((compare_at_price - price) / compare_at_price) * 100).toFixed(0))
    : undefined;

export const getTagValues = (tags: string[]) =>
  tags &&
  tags
    .filter(
      (tag) =>
        tag.startsWith(VALUE.STARTWITH) || tag.startsWith(DIET.STARTWITH),
    )
    .map((tag) =>
      tag
        .replace(VALUE.STARTWITH, '')
        .replace(DIET.STARTWITH, '')
        .toUpperCase(),
    );

export const getIsOriginFranceFromTags = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase().endsWith(VALUES.ORIGINFRANCE));

export const getIsMadeInFranceFromTags = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase().endsWith(VALUES.MADEINFRANCE));

export const getIsFallingPriceFromTags = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase().endsWith(VALUES.FALLINGPRICE));

export const getIsFrozenPriceFromTags = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase().endsWith(VALUES.FROZENPRICE));

export const getIsOriginFranceTagLabel = (tags: string[]) =>
  tags &&
  tags
    .find((tag) => tag.toUpperCase().endsWith(VALUES.ORIGINFRANCE))
    ?.replace(VALUE.STARTWITH, '');

export const getIsMadeInFranceFromTagLabel = (tags: string[]) =>
  tags &&
  tags
    .find((tag) => tag.toUpperCase().endsWith(VALUES.MADEINFRANCE))
    ?.replace(VALUE.STARTWITH, '');

export const getIsNew = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase() === NEW);

export const getIsFresh = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase() === FRESH);

export const getIsUltraFresh = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase() === ULTRA_FRESH);

export const getHasEarlyExpiration = (tags: string[]) =>
  tags && tags.some((tag) => tag.toUpperCase() === EARLY_EXPIRATION);

export const getMultipleProductCount = (tags: string[]) =>
  Number(
    (tags &&
      tags
        .find((tag) => tag.startsWith(PRODUCT_MULTIPLE.STARTWITH))
        ?.split(PRODUCT_MULTIPLE.STARTWITH)[1]) ||
      1,
  );

export const getIsPetProduct = (tags: string[], subType?: string) =>
  (tags && tags.some((tag) => tag.startsWith(RAYONS.ANIMALERIE))) ||
  subType === PRODUCT_SUBTYPE_DE.PETS;

export const getMinimumProductQuantity = (tags: string[]) =>
  Number(
    tags &&
      tags
        .find((tag) => tag.startsWith(MINIMUM_ADD_TO_CART_QUANTITY))
        ?.split('_')[1],
  ) || 1;

export const getMalinDiscount = (tags: Array<string>) => {
  const maybePrice = tags
    .find((tag) => tag.startsWith('customer-tag-discount_member-malin'))
    ?.split('customer-tag-discount_member-malin_')[1];

  if (!maybePrice) {
    return null;
  }

  return parseFloat(maybePrice);
};

const TAG_CATEGORY_PREFIX = 'Catégorie_';

export const getProductCategory = (product: {
  tags: Array<string>;
}): null | string => {
  const categoryTag = product.tags.find((tag) =>
    tag.startsWith(TAG_CATEGORY_PREFIX),
  );

  if (categoryTag == null) {
    return null;
  }

  return categoryTag.split(TAG_CATEGORY_PREFIX)[1];
};

export const formatProductToKlaviyo =
  (appBaseUrl: string) =>
  (
    ecommerceProduct: IProduct | IShoppingCartProduct,
  ): {
    ProductID: string;
    SKU: string;
    ProductName: string;
    ItemPrice: number;
    Quantity: number;
    RowTotal: number;
    ProductURL: string;
    ImageURL: string | undefined;
    Brand: string;
    CompareAtPrice: number;
    ProductCategories: string[];
  } => {
    return {
      ProductID: ecommerceProduct.sku,
      SKU: ecommerceProduct.sku,
      ProductName: ecommerceProduct.title,
      ItemPrice: ecommerceProduct.price,
      Quantity: 'quantity' in ecommerceProduct ? ecommerceProduct.quantity : 0,
      RowTotal:
        'quantity' in ecommerceProduct
          ? ecommerceProduct.price * ecommerceProduct.quantity
          : 0,
      ProductURL: `${appBaseUrl}/products/${ecommerceProduct.handle}`,
      ImageURL: ecommerceProduct.product_image,
      Brand: ecommerceProduct.vendor,
      CompareAtPrice: ecommerceProduct.compare_at_price,
      ProductCategories: ecommerceProduct.collection_titles || [],
    };
  };

export const formatCheckoutProductToKlaviyo = (
  product: IShoppingCartProduct,
) => {
  return {
    product: {
      handle: product.handle,
      title: product.title,
      variant: {
        images: [
          {
            src: product.image,
          },
        ],
      },
    },
    quantity: product.quantity,
    line_price: product.membershipPriceSubtotal,
  };
};

// to remove
export const formatProductShoppingCartToTracking = (
  product: ShoppingCartProductType,
  quantity: number,
): EcommerceProductData => ({
  name: product.title,
  sku: product.sku,
  price: product.price * 100,
  brand: product.vendor,
  category: product.product_type,
  quantity,
  url: `${window.location.origin}/products/${product?.handle ?? ''}`,
  imageUrl: product.image,
  compareAtPrice: product.compare_at_price,
});

export const formatSearchProductToTracking = (
  product: ShoppingCartProductType | IOrderItem,
  quantity: number,
): EcommerceProductData => ({
  name: product.title,
  sku: product.sku,
  price: product.price * 100,
  brand: product.vendor,
  category: product.product_type,
  quantity,
});

const eligibilityRequirements: Array<
  [(tags: Array<string>) => boolean, string]
> = [
  [getIsFresh, FRESH_USER],
  [getIsUltraFresh, ULTRA_FRESH_USER],
];

export const needEligibilityCheck = (
  omsTags: Array<string>,
  product: ShoppingCartProductType,
) => {
  // Fail-fast: return false on the first requirement not met
  return !!eligibilityRequirements.find(
    ([productRequirement, tag]) =>
      productRequirement(product.tags) && !omsTags.includes(tag),
  );
};

const isBundleSaleRateApplicable = (
  quantity: number,
  productBundleNumber: number | null,
  bundleSaleRate: number | null,
  isKit: boolean,
): bundleSaleRate is number => {
  return (
    Boolean(bundleSaleRate) &&
    productBundleNumber !== null &&
    // A kit's promotion is valid from the first unit, so it applies before
    // add-to-cart (whatever the quantity). A bundle only reaches its discount
    // once the in-cart quantity meets the threshold.
    (isKit || quantity >= productBundleNumber)
  );
};

/**
 * A product's promotional-offer kind, derived from its bundle-sale tag
 * (`product-bundle-rate-<threshold>-<rate>`) and `combination_product_data`:
 * - `KIT`: a combination product carrying a bundle-sale tag. Its discount
 *   applies from the first unit, so it shows before add-to-cart.
 * - `BUNDLE`: a bundle-sale tag but no combination data. Its discount only
 *   applies once the in-cart quantity reaches the tag's threshold.
 * - `NONE`: neither.
 */
export const ProductOfferKind = {
  KIT: 'kit',
  BUNDLE: 'bundle',
  NONE: 'none',
} as const;
export type ProductOfferKind =
  (typeof ProductOfferKind)[keyof typeof ProductOfferKind];

/**
 * Classifies a product's promotional offer. Centralised so the pricing path
 * and the display components can never diverge (see LF-1992).
 */
export const getProductOfferKind = (
  product: Pick<IProduct, 'tags' | 'combination_product_data'>,
): ProductOfferKind => {
  const { productBundleNumber, productBundleSale } = getBundleProductDetails(
    product.tags,
  );

  if (productBundleSale && product.combination_product_data) {
    return ProductOfferKind.KIT;
  }

  if (productBundleNumber && productBundleSale) {
    return ProductOfferKind.BUNDLE;
  }

  return ProductOfferKind.NONE;
};

export const getProductOfferFlags = (
  product: Pick<IProduct, 'tags' | 'combination_product_data'>,
): { isKit: boolean; isBundle: boolean } => {
  const kind = getProductOfferKind(product);

  return {
    isKit: kind === ProductOfferKind.KIT,
    isBundle: kind === ProductOfferKind.BUNDLE,
  };
};

export const getProductPricing = (
  product: Pick<
    IProduct,
    'price' | 'compare_at_price' | 'tags' | 'meta' | 'combination_product_data'
  >,
  quantity: number,
  features: ProductPricingFeatures = {
    isCustomerMalin: false,
  },
) => {
  const price = new Decimal(product.price);
  const compareAtPrice = new Decimal(product.compare_at_price);

  let totalSpecialDiscount = new Decimal(0);
  let totalSpecialDiscountInPercentOfMemberPrice = new Decimal(0);

  // STEP 1 -- Flat discounts

  const malinDiscount = new Decimal(getMalinDiscount(product.tags) ?? 0);
  const malinDiscountPercentOfTotal = compareAtPrice
    .minus(compareAtPrice.minus(malinDiscount))
    .div(compareAtPrice);
  const malinDiscountPercentOfMemberPrice = price
    .minus(price.minus(malinDiscount))
    .div(price);

  if (features.isCustomerMalin) {
    totalSpecialDiscount = totalSpecialDiscount
      .plus(malinDiscount)
      .toNearest(0.01);
    totalSpecialDiscountInPercentOfMemberPrice =
      totalSpecialDiscountInPercentOfMemberPrice.plus(
        malinDiscountPercentOfMemberPrice,
      );
  }

  const subTotal = price.minus(totalSpecialDiscount).toNearest(0.01);

  // STEP 2 -- Percent discounts

  const { productBundleNumber, productBundleSale } = getBundleProductDetails(
    product.tags,
  );

  const { isKit } = getProductOfferFlags(product);

  const bundleSaleRate = productBundleSale ? productBundleSale / 100 : null;

  const bundleSaleRateIsApplicable = isBundleSaleRateApplicable(
    quantity,
    productBundleNumber,
    bundleSaleRate,
    isKit,
  );

  const bundleSaleDiscount = bundleSaleRateIsApplicable
    ? subTotal.times(bundleSaleRate).toNearest(0.01)
    : new Decimal(0);
  const bundleSaleDiscountPercentOfTotal = compareAtPrice
    .minus(compareAtPrice.minus(bundleSaleDiscount))
    .div(compareAtPrice);
  const bundleSaleDiscountPercentOfMemberPrice = price
    .minus(price.minus(bundleSaleDiscount))
    .div(price);

  if (bundleSaleRate) {
    totalSpecialDiscount = totalSpecialDiscount
      .plus(bundleSaleDiscount)
      .toNearest(0.01);
    totalSpecialDiscountInPercentOfMemberPrice =
      totalSpecialDiscountInPercentOfMemberPrice.plus(
        bundleSaleDiscountPercentOfMemberPrice,
      );
  }

  const totalPrice = price.minus(totalSpecialDiscount).toNearest(0.01);

  // If present, calculate the new unit price according to special discount.
  // We use the percent of the member price to calculate because unit price is based on member price.
  const unitPrice =
    product.meta.finance?.unit_price != null
      ? new Decimal(product.meta.finance.unit_price)
      : null;
  const unitPriceDiscounted = unitPrice
    ? unitPrice.times(
        new Decimal(1).minus(totalSpecialDiscountInPercentOfMemberPrice),
      )
    : null;

  return {
    price: totalPrice.toNearest(0.01).toNumber(),
    compareAtPrice: compareAtPrice.toNearest(0.01).toNumber(),
    unitPrice: unitPriceDiscounted?.toNearest(0.01).toNumber() ?? null,
    discountPercent: product.compare_at_price
      ? compareAtPrice
          .minus(totalPrice)
          .div(compareAtPrice)
          .times(100)
          .toFixed(0)
      : null,
    breakdown: {
      memberDiscountPercent: getDiscountPercent(product),
      malinDiscountPercent: malinDiscountPercentOfTotal.toNumber(),
      bundleSaleDiscountPercent: bundleSaleDiscountPercentOfTotal.toNumber(),
    },
  };
};
